import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { Search, Filter, Plus, FileText } from 'lucide-react';
import { fetchInvoices } from '@/lib/api';
import type { Invoice } from '@/types';
import { DataTable } from '@/components/ui/data-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { InvoiceForm } from '@/components/invoices/InvoiceForm';
import { formatCurrency, formatDate, capitalizeFirst } from '@/lib/utils';
import { STATUS_OPTIONS, getInvoiceStatusBadgeVariant } from '@/lib/invoice-utils';



export function InvoicesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const prefillPlacementId = searchParams.get('placementId') ?? undefined;
  const prefillCompanyId = searchParams.get('companyId') ?? undefined;
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(
    Boolean(prefillPlacementId || prefillCompanyId),
  );
  const pageSize = 10;

  // Debounce the search input with a properly-cleaned-up timeout.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [search]);

  const params = useMemo(() => ({
    page,
    limit: pageSize,
    status: statusFilter !== 'all' ? statusFilter : undefined,
  }), [page, statusFilter]);

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', params],
    queryFn: () => fetchInvoices(params),
  });

  // Client-side filtering for company search
  const filteredData = useMemo(() => {
    if (!data?.data || !debouncedSearch) return data?.data ?? [];
    const q = debouncedSearch.toLowerCase();
    return data.data.filter((inv) =>
      inv.invoiceNumber.toLowerCase().includes(q) ||
      inv.company.name.toLowerCase().includes(q)
    );
  }, [data?.data, debouncedSearch]);

  const columns: ColumnDef<Invoice>[] = useMemo(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Invoice #',
        cell: ({ row }) => {
          const invoice = row.original;
          return (
            <button
              onClick={() => navigate(`/invoices/${invoice.id}`)}
              className="font-medium hover:text-emerald-500 flex items-center gap-2"
            >
              <FileText className="h-4 w-4 text-text-tertiary" />
              {invoice.invoiceNumber}
            </button>
          );
        },
      },
      {
        accessorKey: 'company',
        header: 'Company',
        cell: ({ row }) => {
          const invoice = row.original;
          return (
            <span className="text-sm">{invoice.company.name}</span>
          );
        },
      },
      {
        accessorKey: 'issueDate',
        header: 'Issue Date',
        cell: ({ row }) => {
          return <span className="text-sm">{formatDate(row.original.issueDate)}</span>;
        },
      },
      {
        accessorKey: 'dueDate',
        header: 'Due Date',
        cell: ({ row }) => {
          const dueDate = row.original.dueDate;
          if (!dueDate) return <span className="text-text-tertiary">-</span>;
          const isOverdue =
            row.original.status !== 'paid' && new Date(dueDate) < new Date();
          return (
            <span className={`text-sm ${isOverdue ? 'text-red-500 font-medium' : ''}`}>
              {formatDate(dueDate)}
            </span>
          );
        },
      },
      {
        accessorKey: 'totalAmount',
        header: 'Total',
        cell: ({ row }) => {
          return (
            <span className="font-medium">
              {formatCurrency(row.original.totalAmount, row.original.currency)}
            </span>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const status = row.original.status;
          return (
            <Badge variant={getInvoiceStatusBadgeVariant(status)}>
              {capitalizeFirst(status)}
            </Badge>
          );
        },
      },
    ],
    [navigate],
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Invoices</h1>
          <p className="text-text-secondary mt-1">
            Manage and track invoices for placements
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Invoice
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-tertiary" />
            <Input
              placeholder="Search by invoice number or company..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Status Filter */}
          <div className="flex gap-2">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-text-tertiary" />
            </div>

            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(value);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-bg-tertiary rounded-xl border border-white/10 shadow-raised surface-sheen">
        <DataTable
          columns={columns}
          data={debouncedSearch ? filteredData : (data?.data ?? [])}
          page={page}
          pageSize={pageSize}
          total={debouncedSearch ? filteredData.length : (data?.pagination.total ?? 0)}
          onPageChange={setPage}
          isLoading={isLoading}
          emptyMessage="No invoices found. Try adjusting your filters."
          getRowId={(row) => row.id}
        />
      </div>

      {/* Create Invoice Dialog */}
      <InvoiceForm
        open={showCreateDialog}
        onOpenChange={(next) => {
          setShowCreateDialog(next);
          // Clear the deep-link query params once the dialog is closed so a
          // refresh doesn't re-open it with stale pre-selection.
          if (!next && (prefillPlacementId || prefillCompanyId)) {
            const cleared = new URLSearchParams(searchParams);
            cleared.delete('placementId');
            cleared.delete('companyId');
            setSearchParams(cleared, { replace: true });
          }
        }}
        companyId={prefillCompanyId}
        placementId={prefillPlacementId}
        onSuccess={() => {
          setShowCreateDialog(false);
        }}
      />
    </div>
  );
}
